import React from 'react';
import styled from 'styled-components';

interface ServicesProps {
  onBooksClick?: () => void;
  onVideoLearningClick?: () => void;
  onBlogsClick?: () => void;
  onMkAcademyOnlineClick?: () => void;
}

const ServicesSection = styled.section`
  padding: 4rem 0;
  background: white;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 3rem;
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 1rem;
`;

const SectionSubtitle = styled.p`
  font-size: 1.1rem;
  color: #64748b;
  max-width: 600px;
  margin: 0 auto;
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.5rem;
  
  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const ServiceCard = styled.div`
  background: #f8fafc;
  border-radius: 15px;
  padding: 1.5rem;
  text-align: center;
  transition: all 0.3s ease;
  border: 1px solid #e2e8f0;
  cursor: pointer;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.1);
    background: white;
  }

  &:active {
    transform: translateY(-2px);
  }
`;

const ServiceIcon = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: linear-gradient(135deg, #1e3a8a 0%, #1e40af 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1rem;
  font-size: 1.5rem;
  color: white;
`;

const ServiceTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 0.75rem;
`;

const ServiceDescription = styled.p`
  color: #64748b;
  line-height: 1.5;
  font-size: 0.9rem;
`;

const Services: React.FC<ServicesProps> = ({ 
  onBooksClick, 
  onVideoLearningClick, 
  onBlogsClick, 
  onMkAcademyOnlineClick 
}) => {
  const services = [
    {
      id: 1,
      icon: '📚',
      title: 'Books',
      description: 'Comprehensive textbooks with interactive exercises and real-time feedback to enhance your learning experience.'
    },
    {
      id: 2,
      icon: '🎥',
      title: 'Video Lessons',
      description: 'Engaging video tutorials that break down complex concepts into easy-to-understand segments.'
    },
    {
      id: 3,
      icon: '🏫',
      title: 'MK Academy',
      description: 'Comprehensive online and in-person classes to enhance your learning with expert instruction and structured curriculum.'
    },
    {
      id: 4,
      icon: '📝',
      title: 'Blogs',
      description: 'Read educational insights, tips, and stories from our math education experts and community.'
    }
  ];

  return (
    <ServicesSection id="our-promise">
      <Container>
        <SectionHeader>
          <SectionTitle>Our Services</SectionTitle>
          <SectionSubtitle>
            Comprehensive learning solutions designed to help you master mathematics at your own pace
          </SectionSubtitle>
        </SectionHeader>

        <ServicesGrid>
          {services.map((service) => {
            const getClickHandler = () => {
              switch (service.id) {
                case 1: return onBooksClick;
                case 2: return onVideoLearningClick;
                case 3: return onMkAcademyOnlineClick;
                case 4: return onBlogsClick;
                default: return undefined;
              }
            };

            return (
              <ServiceCard 
                key={service.id}
                onClick={getClickHandler() || (() => console.log(`${service.title} navigation not available`))}
              >
                <ServiceIcon>{service.icon}</ServiceIcon>
                <ServiceTitle>{service.title}</ServiceTitle>
                <ServiceDescription>{service.description}</ServiceDescription>
              </ServiceCard>
            );
          })}
        </ServicesGrid>
      </Container>
    </ServicesSection>
  );
};

export default Services; 